// components/Header.js – Redesigned to match Hiring Geeta reference UI

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useMediaQuery,
  useTheme,
  Drawer,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  InputBase,
} from "@mui/material";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import WorkIcon from "@mui/icons-material/Work";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import FolderIcon from "@mui/icons-material/Folder";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useATS } from "../context/ATSContext";

// ─── Inject styles once ───────────────────────────────────────────────────────
const STYLE_ID = "hb-header-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    :root {
      --hb-teal: #0d9488;
      --hb-teal-dark: #0f766e;
      --hb-text: #1a1a2e;
      --hb-muted: #6b7280;
      --hb-border: #e5e7eb;
      --hb-bg: #ffffff;
      --hb-hover: #f3f4f6;
      --hb-font: 'Inter', -apple-system, sans-serif;
    }

    .hb-bar {
      position: sticky;
      top: 0;
      z-index: 1200;
      width: 100%;
      background: #ffffff;
      border-bottom: 1px solid var(--hb-border);
      box-shadow: 0 1px 3px rgba(0,0,0,0.06);
    }

    .hb-inner {
      display: flex;
      align-items: center;
      padding: 0 32px;
      height: 60px;
      gap: 32px;
    }

    /* ── Logo ── */
    .hb-logo {
      display: flex;
      align-items: center;
      gap: 0;
      cursor: pointer;
      text-decoration: none;
      flex-shrink: 0;
    }

    .hb-logo-wordmark {
      font-family: var(--hb-font);
      font-weight: 700;
      font-size: 18px;
      color: var(--hb-teal);
      line-height: 1;
      letter-spacing: -0.3px;
    }

    /* ── Nav ── */
    .hb-nav {
      display: flex;
      align-items: center;
      gap: 0;
      flex: 1;
    }

    .hb-nav-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 14px;
      border: none;
      background: transparent;
      color: var(--hb-muted);
      font-family: var(--hb-font);
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: color 0.15s;
      white-space: nowrap;
      line-height: 1;
      position: relative;
      text-decoration: none;
    }

    .hb-nav-btn:hover {
      color: var(--hb-text);
    }

    .hb-nav-btn.active {
      color: var(--hb-teal);
      font-weight: 600;
    }

    .hb-nav-btn.active::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--hb-teal);
      border-radius: 2px 2px 0 0;
    }

    .hb-chevron {
      font-size: 16px !important;
      color: var(--hb-muted);
      transition: transform 0.2s;
    }

    .hb-chevron.open { transform: rotate(180deg); }

    /* ── Dropdown ── */
    .hb-dropdown {
      position: absolute;
      top: calc(100% + 12px);
      left: 0;
      background: #fff;
      border-radius: 10px;
      border: 1px solid var(--hb-border);
      box-shadow: 0 8px 24px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.04);
      padding: 6px;
      min-width: 190px;
      z-index: 1400;
      animation: hbDropIn 0.15s ease;
    }

    @keyframes hbDropIn {
      from { opacity: 0; transform: translateY(-6px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .hb-drop-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 8px 12px;
      border-radius: 7px;
      border: none;
      background: transparent;
      color: #374151;
      font-family: var(--hb-font);
      font-size: 13.5px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.12s;
      text-align: left;
    }

    .hb-drop-item:hover {
      background: var(--hb-hover);
      color: var(--hb-teal);
    }

    .hb-drop-item svg {
      font-size: 16px !important;
      color: var(--hb-teal);
      flex-shrink: 0;
    }

    /* ── Right area ── */
    .hb-right {
      display: flex;
      align-items: center;
      gap: 4px;
      flex-shrink: 0;
    }

    /* ── Icon buttons ── */
    .hb-icon-btn {
      width: 34px;
      height: 34px;
      border-radius: 7px;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #6b7280;
      transition: background 0.12s, color 0.12s;
    }

    .hb-icon-btn:hover {
      background: var(--hb-hover);
      color: var(--hb-text);
    }

    .hb-icon-btn svg { font-size: 20px !important; }

    /* ── Avatar ── */
    .hb-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      border: none;
      background: #e5e7eb;
      cursor: pointer;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.15s;
    }

    .hb-avatar:hover { opacity: 0.85; }

    .hb-avatar-initials {
      font-family: var(--hb-font);
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    /* ── Hamburger ── */
    .hb-hamburger {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 8px;
      border: 1px solid var(--hb-border);
      background: transparent;
      cursor: pointer;
      color: var(--hb-muted);
      transition: background 0.15s;
    }

    .hb-hamburger:hover { background: var(--hb-hover); }

    /* ── Mobile drawer ── */
    .hb-drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px 20px 12px;
      border-bottom: 1px solid var(--hb-border);
    }

    .hb-drawer-wordmark {
      font-family: var(--hb-font);
      font-weight: 700;
      font-size: 18px;
      color: var(--hb-teal);
    }

    .hb-drawer-close {
      width: 30px; height: 30px;
      border-radius: 7px;
      border: 1px solid var(--hb-border);
      background: transparent;
      cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: var(--hb-muted);
    }

    .hb-drawer-nav { padding: 10px 12px; flex: 1; overflow-y: auto; }

    .hb-drawer-item {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 9px 12px;
      border-radius: 8px; border: none; background: transparent;
      color: #374151; font-family: var(--hb-font); font-size: 14px; font-weight: 500;
      cursor: pointer; transition: background 0.12s, color 0.12s;
      text-align: left; margin-bottom: 1px;
    }

    .hb-drawer-item:hover, .hb-drawer-item.active {
      background: rgba(13,148,136,0.07);
      color: var(--hb-teal);
    }

    .hb-drawer-item svg { font-size: 18px !important; flex-shrink: 0; color: #9ca3af; }
    .hb-drawer-item.active svg, .hb-drawer-item:hover svg { color: var(--hb-teal); }

    .hb-drawer-expand {
      display: flex; align-items: center; gap: 10px;
      width: 100%; padding: 9px 12px;
      border-radius: 8px; border: none; background: transparent;
      color: #374151; font-family: var(--hb-font); font-size: 14px; font-weight: 500;
      cursor: pointer; transition: background 0.12s;
      margin-bottom: 1px;
    }

    .hb-drawer-expand:hover { background: var(--hb-hover); }
    .hb-drawer-expand svg { font-size: 18px !important; flex-shrink: 0; color: #9ca3af; }

    .hb-drawer-sub { padding-left: 14px; }

    .hb-drawer-sub-item {
      display: flex; align-items: center; gap: 9px;
      width: 100%; padding: 8px 12px;
      border-radius: 8px; border: none; background: transparent;
      color: #6b7280; font-family: var(--hb-font); font-size: 13.5px; font-weight: 500;
      cursor: pointer; transition: background 0.12s, color 0.12s;
      margin-bottom: 1px; text-align: left;
    }

    .hb-drawer-sub-item:hover { background: rgba(13,148,136,0.06); color: var(--hb-teal); }
    .hb-drawer-sub-item svg { font-size: 16px !important; flex-shrink: 0; }

    .hb-drawer-divider { height: 1px; background: var(--hb-border); margin: 6px 2px; }
  `;
  document.head.appendChild(s);
}

// ─── Dropdown helper ──────────────────────────────────────────────────────────
const DropdownMenu = ({ items, onNavigate }) => (
  <div className="hb-dropdown">
    {items.map((item) => (
      <button
        key={item.path}
        className="hb-drop-item"
        onClick={() => onNavigate(item.path)}
      >
        <item.icon style={{ fontSize: 16 }} />
        {item.label}
      </button>
    ))}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [assessmentsOpen, setAssessmentsOpen] = useState(false);
  const [sopOpen, setSopOpen] = useState(false);

  const assessDropRef = useRef(null);
  const sopDropRef = useRef(null);

  const { userRole } = useATS();

  useEffect(() => {
    const handler = (e) => {
      if (assessDropRef.current && !assessDropRef.current.contains(e.target))
        setAssessmentsOpen(false);
      if (sopDropRef.current && !sopDropRef.current.contains(e.target))
        setSopOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleAssessmentsToggle = () => {
    setAssessmentsOpen((v) => !v);
    setSopOpen(false);
  };
  const handleSopToggle = () => {
    setSopOpen((v) => !v);
    setAssessmentsOpen(false);
  };
  const handleSwitchToDailyTick = () => navigate("/home");

  const goTo = (path) => {
    navigate(path);
    setAssessmentsOpen(false);
    setSopOpen(false);
    setMobileOpen(false);
  };

  const adminNavItems = [
    { label: "Assessment Folders", path: "/ats/assessments", icon: FolderIcon },
    {
      label: "Assessment Report",
      path: "/ats/assessments/report",
      icon: AssessmentIcon,
    },
  ];
  const staffNavItems = [
    {
      label: "My Assessments",
      path: "/ats/my-assessments",
      icon: AssessmentIcon,
    },
  ];
  const assessmentItems =
    userRole === "admin"
      ? adminNavItems
      : userRole === "staff"
      ? staffNavItems
      : [...adminNavItems, ...staffNavItems];

  const sopAdminItems = [
    { label: "SOP Folders", path: "/ats/sop", icon: MenuBookIcon },
  ];
  const sopStaffItems = [
    { label: "SOP Library", path: "/ats/sop", icon: MenuBookIcon },
  ];
  const sopItems = userRole === "staff" ? sopStaffItems : sopAdminItems;

  const navItems = [
    { label: "Dashboard", path: "/ats", icon: DashboardIcon },
    { label: "Templates", path: "/ats/templates", icon: DescriptionIcon },
  ];

  const isActive = (path) => location.pathname === path;

  // ── Desktop nav ──────────────────────────────────────────────────────────
  const desktopNav = (
    <nav className="hb-nav">
      {navItems.map((item) => (
        <button
          key={item.path}
          className={`hb-nav-btn${isActive(item.path) ? " active" : ""}`}
          onClick={() => goTo(item.path)}
        >
          {item.label}
        </button>
      ))}

      {/* Assessments dropdown */}
      <div ref={assessDropRef} style={{ position: "relative" }}>
        <button
          className={`hb-nav-btn${assessmentsOpen ? " active" : ""}`}
          onClick={handleAssessmentsToggle}
        >
          Assessments
          <ExpandMore
            className={`hb-chevron${assessmentsOpen ? " open" : ""}`}
          />
        </button>
        {assessmentsOpen && (
          <DropdownMenu items={assessmentItems} onNavigate={goTo} />
        )}
      </div>

      {/* SOP dropdown */}
      <div ref={sopDropRef} style={{ position: "relative" }}>
        <button
          className={`hb-nav-btn${sopOpen ? " active" : ""}`}
          onClick={handleSopToggle}
        >
          SOPs
          <ExpandMore className={`hb-chevron${sopOpen ? " open" : ""}`} />
        </button>
        {sopOpen && <DropdownMenu items={sopItems} onNavigate={goTo} />}
      </div>
    </nav>
  );

  // ── Mobile drawer ─────────────────────────────────────────────────────────
  const drawer = (
    <div
      style={{
        width: 270,
        height: "100%",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div className="hb-drawer-header">
        <span className="hb-drawer-wordmark">Hiring Geeta</span>
        <button className="hb-drawer-close" onClick={handleDrawerToggle}>
          <CloseIcon style={{ fontSize: 16 }} />
        </button>
      </div>
      <div className="hb-drawer-nav">
        {navItems.map((item) => (
          <button
            key={item.path}
            className={`hb-drawer-item${isActive(item.path) ? " active" : ""}`}
            onClick={() => goTo(item.path)}
          >
            <item.icon style={{ fontSize: 18 }} />
            {item.label}
          </button>
        ))}
        <div className="hb-drawer-divider" />
        <button className="hb-drawer-expand" onClick={handleAssessmentsToggle}>
          <AssessmentIcon style={{ fontSize: 18 }} />
          <span style={{ flex: 1, textAlign: "left" }}>Assessments</span>
          {assessmentsOpen ? (
            <ExpandLess style={{ fontSize: 16 }} />
          ) : (
            <ExpandMore style={{ fontSize: 16 }} />
          )}
        </button>
        {assessmentsOpen && (
          <div className="hb-drawer-sub">
            {assessmentItems.map((item) => (
              <button
                key={item.path}
                className="hb-drawer-sub-item"
                onClick={() => goTo(item.path)}
              >
                <item.icon style={{ fontSize: 16 }} />
                {item.label}
              </button>
            ))}
          </div>
        )}
        <button className="hb-drawer-expand" onClick={handleSopToggle}>
          <MenuBookIcon style={{ fontSize: 18 }} />
          <span style={{ flex: 1, textAlign: "left" }}>SOPs</span>
          {sopOpen ? (
            <ExpandLess style={{ fontSize: 16 }} />
          ) : (
            <ExpandMore style={{ fontSize: 16 }} />
          )}
        </button>
        {sopOpen && (
          <div className="hb-drawer-sub">
            {sopItems.map((item) => (
              <button
                key={item.path}
                className="hb-drawer-sub-item"
                onClick={() => goTo(item.path)}
              >
                <item.icon style={{ fontSize: 16 }} />
                {item.label}
              </button>
            ))}
          </div>
        )}
        <div className="hb-drawer-divider" />
        <button className="hb-drawer-item" onClick={handleSwitchToDailyTick}>
          <SwapHorizIcon style={{ fontSize: 18 }} />
          Switch to Daily Tick
        </button>
      </div>
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <header className="hb-bar">
        <div className="hb-inner">
          {/* Logo */}
          <div className="hb-logo" onClick={() => navigate("/ats")}>
            <span className="hb-logo-wordmark">Hiring Geeta</span>
          </div>

          {/* Desktop nav */}
          {!isMobile && desktopNav}

          {/* Right side */}
          <div className="hb-right">
            <Tooltip title="Notifications">
              <button className="hb-icon-btn">
                <NotificationsNoneIcon />
              </button>
            </Tooltip>

            <Tooltip title="Help">
              <button className="hb-icon-btn">
                <HelpOutlineIcon />
              </button>
            </Tooltip>

            <Tooltip title="Settings">
              <button className="hb-icon-btn">
                <SettingsIcon />
              </button>
            </Tooltip>

            <Tooltip title="Account">
              <button className="hb-avatar" onClick={handleMenuOpen}>
                <span className="hb-avatar-initials">HR</span>
              </button>
            </Tooltip>

            {isMobile && (
              <button className="hb-hamburger" onClick={handleDrawerToggle}>
                <MenuIcon style={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Account menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
            minWidth: 150,
            fontFamily: "'Inter', sans-serif",
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={handleMenuClose}
          sx={{ fontSize: 14, fontFamily: "'Inter', sans-serif" }}
        >
          Profile
        </MenuItem>
        <MenuItem
          onClick={handleMenuClose}
          sx={{ fontSize: 14, fontFamily: "'Inter', sans-serif" }}
        >
          Settings
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleSwitchToDailyTick}
          sx={{
            fontSize: 14,
            fontFamily: "'Inter', sans-serif",
            color: "#0d9488",
          }}
        >
          Switch to Daily Tick
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleMenuClose}
          sx={{
            fontSize: 14,
            color: "#ef4444",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Sign out
        </MenuItem>
      </Menu>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: { width: 270, borderLeft: "1px solid #e5e7eb" } }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
